/**
 * PromotionReadinessReport.js
 *
 * Promotion is driven by students.skill_level_id (set in Student Master).
 * If not set, falls back to batch level_id.
 *
 * Thresholds:
 *   Assigned Beginner     → promote when avg >= 4
 *   Assigned Intermediate → promote when avg >= 7
 *   Assigned Advanced     → promote when avg >= 9
 *   Assigned Elite        → top level
 *
 * If student's skill_level differs from their batch level → mismatch warning shown.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  FaTrophy, FaArrowUp, FaArrowDown, FaMinus,
  FaFilter, FaCheckCircle, FaSearch, FaSyncAlt,
  FaChevronDown, FaChevronUp, FaArrowRight, FaLock,
  FaExclamationTriangle,
} from "react-icons/fa";
import { Colors } from "./branding";
import {useTheme, getT, glassCard, getBrandColors, lighten} from "./ThemeContext";
import API_BASE from "./apiConfig";
import { useSkillLevels } from "./hooks/useLookups";
import { useSort } from "./hooks/useSort";
import PageHeaderIcon from "./components/PageHeaderIcon";
const BASE = API_BASE;

const T = {
  blue:     Colors.primaryBlue || "#034e7b",
  dark:     Colors.accentBlue  || "#02304f",
  yellow:   Colors.logoYellow  || "#f7ce0b",
  gray:     "#8899aa",
  font:     "'Segoe UI', system-ui, sans-serif",
  pageBg:   "linear-gradient(160deg,#e8f0f8 0%,#dce8f2 50%,#e4ecf5 100%)",
  glass:    "rgba(255,255,255,0.80)",
  glassBdr: "1px solid rgba(255,255,255,0.60)",
  glassSh:  "0 4px 24px rgba(0,40,100,0.09)",
  topBar:   `linear-gradient(135deg,${getBrandColors().blueDark} 0%,${getBrandColors().blue} 45%,${lighten(getBrandColors().blue,30)} 75%,${lighten(getBrandColors().blue,45)} 100%)`,
};

// gc is now computed per-component via glassCard(T)

// Emoji is purely decorative (not stored in the DB) — keyed by level_order so it
// still lines up correctly if a tenant renames or reorders their skill levels.
const EMOJI_BY_ORDER = { 1: "🌱", 2: "⚡", 3: "🔥", 4: "👑" };
const EMOJI_FALLBACK = "⭐";

// Builds the LEVELS/AVG_BANDS shape this component expects, from the live
// skill_levels table (useSkillLevels()) instead of hardcoded constants — this
// is also what keeps this report's promotion thresholds in sync with
// ParentDashboard.js, which reads the same table.
function buildLevels(skillLevels) {
  return skillLevels.map((lv, i) => ({
    id: lv.level_id,
    label: lv.level_name,
    emoji: EMOJI_BY_ORDER[lv.level_order] || EMOJI_FALLBACK,
    color: lv.color_hex || "#3b82f6",
    bg: (lv.color_hex || "#3b82f6") + "1A",
    promoteAt: lv.promotion_threshold != null ? Number(lv.promotion_threshold) : null,
    next: skillLevels[i + 1]?.level_name || null,
  }));
}
function buildAvgBands(levels) {
  let lo = 0;
  return levels.map(l => {
    const hi = l.promoteAt != null ? l.promoteAt : 10;
    const band = { min: lo, max: hi, color: l.color, bg: l.bg, label: l.label, emoji: l.emoji };
    lo = hi;
    return band;
  });
}
// Module-level (not component-scoped) since ScorePill/StudentRow are siblings
// of the main component, not nested inside it — each caller passes its own
// live LEVELS/AVG_BANDS down as a prop.
const bandOf  = (avg, avgBands) => avgBands.find(b => avg >= b.min && avg < b.max) || avgBands[avgBands.length - 1] || null;
const levelOf = (id, levels)    => levels.find(l => l.id === id) || null;

// ── Micro helpers ─────────────────────────────────────────────────────────────
function TrendIcon({ trend }) {
  if (trend === "up")     return <FaArrowUp   size={11} color="#10b981" />;
  if (trend === "down")   return <FaArrowDown size={11} color="#ef4444" />;
  if (trend === "stable") return <FaMinus     size={11} color="#f59e0b" />;
  return <span style={{ color: T.gray, fontSize: 11 }}>—</span>;
}

function ScorePill({ avg, avgBands }) {
  const b = bandOf(avg, avgBands);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 12px", borderRadius: 20, fontSize: 13,
      fontWeight: 900, background: b.bg, color: b.color,
      border: `1.5px solid ${b.color}44`, fontFamily: T.font,
    }}>
      {b.emoji} {avg.toFixed(2)}
    </span>
  );
}

function LevelBadge({ name, color }) {
  const hex = color || "#8899aa";
  return (
    <span style={{
      padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700,
      background: hex + "22", color: hex, border: `1px solid ${hex}44`,
      whiteSpace: "nowrap",
    }}>{name || "Not Set"}</span>
  );
}

function MiniBar({ value, max = 10, color }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div style={{ height: 5, borderRadius: 3, background: "rgba(0,0,0,0.08)", overflow: "hidden", minWidth: 60 }}>
      <div style={{ width: `${pct}%`, height: "100%", borderRadius: 3, background: color, transition: "width 0.5s" }} />
    </div>
  );
}

// ── Level summary card ────────────────────────────────────────────────────────
function LevelCard({ level, students, active, onClick }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const gc = glassCard(T);
  const ready   = students.filter(s => s.ready_for_promotion).length;
  const notReady = students.length - ready;
  return (
    <div onClick={onClick} style={{
      ...gc, padding: "18px 22px", cursor: "pointer", WebkitTapHighlightColor:"transparent",
      borderTop: `4px solid ${level.color}`,
      outline: active ? `2px solid ${level.color}` : "none",
      outlineOffset: 2, transition: "transform 0.15s",
    }}
      onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
      onMouseLeave={e => e.currentTarget.style.transform = "none"}
    >
      {/* Big icon on left, all text on right */}
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{
          width: 60, height: 60, borderRadius: 18, flexShrink: 0,
          background: level.bg || `${level.color}18`,
          border: `2px solid ${level.color}44`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 34,
        }}>
          {level.emoji}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 2 }}>
            <span style={{ fontSize: 30, fontWeight: 900, color: level.color, lineHeight: 1 }}>{students.length}</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: T.textPrimary, fontFamily: T.font }}>{level.label}</span>
          </div>
          <div style={{ fontSize: 11, color: T.gray, marginBottom: 8 }}>
            {level.promoteAt ? `Promote at avg ≥ ${level.promoteAt}` : "Top Level — no further promotion"}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {ready > 0 && (
              <div style={{
                padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                background: "rgba(16,185,129,0.12)", color: getBrandColors().green,
                display: "inline-flex", alignItems: "center", gap: 5, alignSelf: "flex-start",
              }}>
                <FaCheckCircle size={10} /> {ready} ready → {level.next}
              </div>
            )}
            {notReady > 0 && level.promoteAt && (
              <div style={{ fontSize: 11, color: T.gray }}>{notReady} below threshold</div>
            )}
            {!level.promoteAt && ready === 0 && (
              <div style={{ fontSize: 11, color: level.color, fontWeight: 700 }}>All at top level 👑</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Student row ───────────────────────────────────────────────────────────────
function StudentRow({ s, rank, expanded, onToggle, levels, avgBands }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const assignedLevel = levelOf(s.assigned_level_id, levels);
  const gap = s.promote_threshold ? Math.max(0, s.promote_threshold - s.avg_score) : 0;

  return (
    <>
      <tr
        onClick={onToggle}
        style={{
          cursor: "pointer", WebkitTapHighlightColor:"transparent",
          background: s.ready_for_promotion ? "rgba(16,185,129,0.05)" : T.rowEven,
          borderBottom: "1px solid rgba(3,78,123,0.06)",
          borderLeft: `3px solid ${s.ready_for_promotion ? "#10b981" : "transparent"}`,
          transition: "background 0.12s",
        }}
        onMouseEnter={e => e.currentTarget.style.background = "rgba(3,78,123,0.07)"}
        onMouseLeave={e => e.currentTarget.style.background = s.ready_for_promotion ? "rgba(16,185,129,0.05)" : "rgba(255,255,255,0.50)"}
      >
        {/* Rank */}
        <td style={{ padding: "12px 14px", textAlign: "center", width: 44 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 26, height: 26, borderRadius: "50%",
            background: assignedLevel?.bg || "rgba(0,0,0,0.06)",
            color: assignedLevel?.color || T.gray,
            fontSize: 11, fontWeight: 900, fontFamily: T.font,
          }}>{rank}</span>
        </td>

        {/* Student */}
        <td style={{ padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
              background: `linear-gradient(135deg,${T.yellow}cc,${T.yellow}55)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 900, color: T.blue,
            }}>
              {(s.student_name || "?").split(" ").map(w => w[0]).slice(0, 2).join("")}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>{s.student_name}</div>
              <div style={{ fontSize: 11, color: T.gray }}>{s.student_code}</div>
            </div>
          </div>
        </td>

        {/* Batch */}
        <td style={{ padding: "12px 14px", fontSize: 12, color: "#555" }}>
          {s.batch_name || "—"}
        </td>

        {/* Assigned Level (skill_level_id from Student Master) */}
        <td style={{ padding: "12px 14px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <LevelBadge name={s.assigned_level} color={s.assigned_level_color} />
            {/* Mismatch warning: student level ≠ batch level */}
            {s.level_mismatch && (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                fontSize: 10, color: getBrandColors().orange, fontWeight: 700,
              }}>
                <FaExclamationTriangle size={9} color={getBrandColors().orange} />
                Batch: {s.batch_level_name}
              </div>
            )}
          </div>
        </td>

        {/* Avg Score */}
        <td style={{ padding: "12px 14px" }}>
          {s.avg_score > 0
            ? <ScorePill avg={s.avg_score} avgBands={avgBands} />
            : <span style={{ fontSize: 12, color: T.gray }}>No data</span>
          }
        </td>

        {/* Trend */}
        <td style={{ padding: "12px 14px", textAlign: "center" }}>
          <TrendIcon trend={s.trend} />
        </td>

        {/* Promotion Status */}
        <td style={{ padding: "12px 20px", minWidth: 240 }}>
          {!s.assigned_level ? (
            <span style={{ fontSize: 12, color: T.gray }}>No level assigned</span>
          ) : s.ready_for_promotion ? (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "6px 14px", borderRadius: 10,
              background: "rgba(16,185,129,0.12)",
              border: "1.5px solid rgba(16,185,129,0.35)",
            }}>
              <FaCheckCircle size={12} color="#10b981" />
              <span style={{ fontSize: 13, fontWeight: 800, color: getBrandColors().green, fontFamily: T.font }}>
                Eligible → {s.promote_to}
              </span>
            </div>
          ) : !s.promote_threshold ? (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <FaLock size={10} color="#f59e0b" />
              <span style={{ fontSize: 12, fontWeight: 700, color: "#f59e0b" }}>Top Level — Elite</span>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 12, color: T.textPrimary }}>
                Needs avg{" "}
                <strong style={{ color: assignedLevel?.color || T.textSub }}>{s.promote_threshold}</strong>
                {" "}to reach <strong>{s.promote_to}</strong>
              </div>
              <div style={{ fontSize: 11, color: "#ef4444", marginTop: 2 }}>
                Gap: +{gap.toFixed(2)} points needed
              </div>
            </div>
          )}
        </td>

        {/* Expand */}
        <td style={{ padding: "12px 14px", textAlign: "center", color: T.gray }}>
          {expanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
        </td>
      </tr>

      {/* Expanded: category breakdown */}
      {expanded && (
        <tr style={{ background: "rgba(3,78,123,0.02)" }}>
          <td colSpan={8} style={{ padding: "0 14px 18px 60px" }}>
            <div style={{ paddingTop: 14 }}>

              {/* Mismatch detail banner inside expand */}
              {s.level_mismatch && (
                <div style={{
                  display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14,
                  padding: "10px 14px", borderRadius: 10,
                  background: "rgba(217,119,6,0.08)", border: "1.5px solid rgba(217,119,6,0.25)",
                }}>
                  <FaExclamationTriangle size={13} color={getBrandColors().orange} style={{ marginTop: 1 }} />
                  <div style={{ fontSize: 12, color: "#92400e", lineHeight: 1.5 }}>
                    <strong>Level mismatch:</strong> Student Master shows{" "}
                    <strong>{s.assigned_level}</strong>, but enrolled in a{" "}
                    <strong>{s.batch_level_name}</strong> batch ({s.batch_name}).
                    {" "}Promotion eligibility uses the Student Master level.
                    Consider re-enrolling into a {s.assigned_level} batch.
                  </div>
                </div>
              )}

              <div style={{ fontSize: 10, fontWeight: 800, color: T.gray, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
                Category Scores
              </div>

              {(s.categories || []).length === 0 ? (
                <div style={{ fontSize: 12, color: T.gray }}>No performance data recorded yet.</div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 10 }}>
                  {s.categories.map((cat, ci) => (
                    <div key={ci} style={{
                      background: T.glass, borderRadius: 10, padding: "12px 16px",
                      border: `1.5px solid ${cat.color}33`,
                    }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: T.gray, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                        {cat.category}
                      </div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 6 }}>
                        <span style={{ fontSize: 24, fontWeight: 900, color: cat.color, lineHeight: 1 }}>{cat.avg.toFixed(1)}</span>
                        <span style={{ fontSize: 10, color: T.gray }}>/10</span>
                      </div>
                      <MiniBar value={cat.avg} max={10} color={cat.color} />
                      <div style={{ fontSize: 10, color: cat.color, fontWeight: 700, marginTop: 5 }}>{cat.band}</div>
                    </div>
                  ))}
                </div>
              )}

              {s.last_assessment && (
                <div style={{ fontSize: 11, color: T.gray, marginTop: 10 }}>
                  Last assessed: {s.last_assessment}
                  {s.prev_avg != null &&
                    <span style={{ marginLeft: 10 }}>· Previous avg: {s.prev_avg.toFixed(2)}</span>
                  }
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════════════
export default function PromotionReadinessReport() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const gc = glassCard(T);
  const skillLevels = useSkillLevels();
  const LEVELS    = buildLevels(skillLevels);
  const AVG_BANDS = buildAvgBands(LEVELS);
  const [students,    setStudents]    = useState([]);
  const [batches,     setBatches]     = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [filterBatch, setFilterBatch] = useState("");
  const [filterLevel, setFilterLevel] = useState("");
  const [filterReady,   setFilterReady]   = useState(false);
  const [filterMismatch, setFilterMismatch] = useState(false);
  const [search,      setSearch]      = useState("");
  const [expanded,    setExpanded]    = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filterBatch
        ? `${BASE}/reports/promotion-readiness?batch_id=${filterBatch}`
        : `${BASE}/reports/promotion-readiness`;
      const [sr, br] = await Promise.all([
        fetch(url).then(r => r.json()),
        fetch(`${BASE}/reports/batches`).then(r => r.json()),
      ]);
      setStudents(Array.isArray(sr) ? sr : []);
      setBatches(Array.isArray(br)  ? br : []);
    } catch { setStudents([]); }
    finally { setLoading(false); }
  }, [filterBatch]);

  useEffect(() => { load(); }, [load]);

  const filtered = students.filter(s => {
    if (filterLevel && s.assigned_level !== filterLevel) return false;
    if (filterReady    && !s.ready_for_promotion) return false;
    if (filterMismatch && !s.level_mismatch)      return false;
    if (search) {
      const q = search.toLowerCase();
      if (!s.student_name.toLowerCase().includes(q) &&
          !(s.student_code || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const { sorted: sortedFiltered, toggleSort: toggleStudentSort, sortArrow: studentSortArrow } = useSort(filtered, null);

  // Cards group by assigned_level_id
  const levelCards   = LEVELS.map(lv => ({ level: lv, students: students.filter(s => s.assigned_level_id === lv.id) }));
  const totalReady   = students.filter(s => s.ready_for_promotion).length;
  const totalMismatch = students.filter(s => s.level_mismatch).length;
  const toggleRow    = id => setExpanded(e => ({ ...e, [id]: !e[id] }));
  return (
    <div style={{ background: T.pageBg, minHeight: "100dvh", fontFamily: T.font }}>

      {/* ── Page header ── */}
      <div style={{ display:"flex", alignItems:"center", gap:14, padding:"20px 24px 0", marginBottom:18, justifyContent:"space-between", flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <PageHeaderIcon icon={<FaTrophy size={20} color={getBrandColors().blueDark} />} />
          <div>
            <h2 style={{ margin:0, fontSize:20, fontWeight:800, color:T.textPrimary, letterSpacing:"-0.01em" }}>Promotion Readiness Report</h2>
            <p style={{ margin:"2px 0 0", fontSize:12, color:T.gray }}>
              Promotion based on assigned level (Student Master) · avg score must reach next level's threshold
            </p>
          </div>
        </div>
        <button onClick={load} disabled={loading} style={{
          display:"flex", alignItems:"center", gap:6, padding:"8px 16px",
          borderRadius:9, border:`1px solid ${T.yellow}55`, cursor:"pointer", WebkitTapHighlightColor:"transparent",
          background:`${T.yellow}22`, color:T.blue, fontSize:12, fontWeight:700,
        }}>
          <FaSyncAlt size={11} style={{ animation: loading ? "spin 1s linear infinite" : "none" }}/> Refresh
        </button>
      </div>

      <div style={{ maxWidth: 1380, margin: "0 auto", padding: "12px 24px 60px" }}>

        {/* Level summary cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 20 }}>
          {levelCards.map(({ level, students: ls }) => (
            <LevelCard key={level.id} level={level} students={ls}
              active={filterLevel === level.label}
              onClick={() => setFilterLevel(f => f === level.label ? "" : level.label)}
            />
          ))}
        </div>

        {/* Promotion criteria strip */}
        <div style={{ ...gc, padding: "14px 22px", marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.gray, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            Promotion Criteria — Based on Assigned Level in Student Master
          </div>
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap", alignItems: "center" }}>
            {LEVELS.filter(l => l.promoteAt).map(l => (
              <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ padding: "3px 10px", borderRadius: 10, fontSize: 12, fontWeight: 800, background: l.bg, color: l.color }}>
                  {l.emoji} {l.label}
                </span>
                <FaArrowRight size={9} color={T.gray} />
                <span style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary }}>avg ≥ {l.promoteAt}</span>
                <FaArrowRight size={9} color={T.gray} />
                <span style={{ fontSize: 12, color: T.gray }}>{l.next}</span>
              </div>
            ))}
            {LEVELS.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ padding: "3px 10px", borderRadius: 10, fontSize: 12, fontWeight: 800, background: LEVELS[LEVELS.length - 1].bg, color: LEVELS[LEVELS.length - 1].color }}>
                  {LEVELS[LEVELS.length - 1].emoji} {LEVELS[LEVELS.length - 1].label}
                </span>
                <span style={{ fontSize: 12, color: T.gray }}>Top level — no further promotion</span>
              </div>
            )}
          </div>
        </div>

        {/* Alert banners */}
        {totalReady > 0 && (
          <div style={{ ...gc, padding: "13px 22px", marginBottom: 10, display: "flex", alignItems: "center", gap: 12, borderLeft: "4px solid #10b981" }}>
            <FaCheckCircle size={18} color="#10b981" />
            <span style={{ fontWeight: 800, color: T.textPrimary, fontSize: 14 }}>
              {totalReady} student{totalReady !== 1 ? "s" : ""} eligible for promotion
            </span>
            <span style={{ color: T.gray, fontSize: 13 }}>
              — avg score has reached the next level's threshold
            </span>
          </div>
        )}

        {totalMismatch > 0 && (
          <div style={{ ...gc, padding: "13px 22px", marginBottom: 10, display: "flex", alignItems: "center", gap: 12, borderLeft: `4px solid ${getBrandColors().orange}` }}>
            <FaExclamationTriangle size={16} color={getBrandColors().orange} />
            <span style={{ fontWeight: 800, color: T.textPrimary, fontSize: 14 }}>
              {totalMismatch} student{totalMismatch !== 1 ? "s" : ""} have a level mismatch
            </span>
            <span style={{ color: T.gray, fontSize: 13 }}>
              — assigned level in Student Master differs from their enrolled batch level
            </span>
          </div>
        )}

        {/* Filters */}
        <div style={{ ...gc, padding: "14px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <FaFilter size={12} color={T.gray} />
          <div style={{ position: "relative" }}>
            <FaSearch size={11} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: T.gray }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search student…"
              style={{ paddingLeft: 30, paddingRight: 12, paddingTop: 8, paddingBottom: 8, borderRadius: 8, border: "1.5px solid rgba(3,78,123,0.15)", fontSize: 13, width: 180, outline: "none" }} />
          </div>
          <select value={filterBatch} onChange={e => setFilterBatch(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid rgba(3,78,123,0.15)", fontSize: 13 , colorScheme: isDark ? "dark" : "light" }}>
            <option value="">All Batches</option>
            {batches.map(b => <option key={b.batch_id} value={b.batch_id}>{b.batch_name}</option>)}
          </select>
          <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid rgba(3,78,123,0.15)", fontSize: 13 , colorScheme: isDark ? "dark" : "light" }}>
            <option value="">All Assigned Levels</option>
            {LEVELS.map(l => <option key={l.id} value={l.label}>{l.emoji} {l.label}</option>)}
          </select>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", WebkitTapHighlightColor:"transparent", fontSize: 13, color: T.textPrimary, fontWeight: 600 }}>
            <input type="checkbox" checked={filterReady} onChange={e => setFilterReady(e.target.checked)} />
            Eligible only
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", WebkitTapHighlightColor:"transparent", fontSize: 13, color: getBrandColors().orange, fontWeight: 600 }}>
            <input type="checkbox" checked={filterMismatch} onChange={e => setFilterMismatch(e.target.checked)} />
            ⚠️ Mismatch only
          </label>
          <span style={{ marginLeft: "auto", fontSize: 12, color: T.gray }}>
            {filtered.length} student{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Table */}
        <div style={{ ...gc, overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: 60, textAlign: "center", color: T.gray, fontSize: 14 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 60, textAlign: "center", color: T.gray, fontSize: 14 }}>No students found.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "rgba(3,78,123,0.05)" }}>
                  {[
                    { label: "#", key: null },
                    { label: "Student", key: "student_name" },
                    { label: "Batch", key: "batch_name" },
                    { label: "Assigned Level", key: "assigned_level" },
                    { label: "Avg Score", key: "avg_score" },
                    { label: "Trend", key: "trend" },
                    { label: "Promotion Status", key: null },
                    { label: "", key: null },
                  ].map((h, i) => (
                    <th key={i} onClick={h.key ? () => toggleStudentSort(h.key) : undefined} style={{
                      padding: "12px 14px", fontSize: 10, fontWeight: 800, color: T.gray,
                      textTransform: "uppercase", letterSpacing: "0.07em",
                      textAlign: i === 0 || i === 5 || i === 7 ? "center" : "left",
                      borderBottom: "2px solid rgba(3,78,123,0.08)", whiteSpace: "nowrap",
                      ...(h.key ? { cursor: "pointer", userSelect: "none" } : {}),
                    }}>{h.label}{h.key ? studentSortArrow(h.key) : ""}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedFiltered.map((s, i) => (
                  <StudentRow
                    key={s.student_id} s={s} rank={i + 1}
                    expanded={!!expanded[s.student_id]}
                    onToggle={() => toggleRow(s.student_id)}
                    levels={LEVELS} avgBands={AVG_BANDS}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer note */}
        <div style={{ marginTop: 14, fontSize: 11, color: T.gray, textAlign: "center" }}>
          Assigned Level is set in Student Master · Batch Level is the group they train in · ⚠️ icon = mismatch between the two
        </div>

      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
